/**
 * Custom Bundle Builder
 * Handles Power Cookie bundle customization with native Shopify subscriptions integration
 */

(function() {
  'use strict';

  // Namespace for the bundle builder
  window.CustomBundleBuilder = window.CustomBundleBuilder || {};

  // Configuration defaults
  const CONFIG = {
    // Default bulk discount tiers (percentage off)
    // Will be overridden by data attributes from Liquid
    defaultBulkDiscounts: [
      { min: 40, discountPercent: 20 },
      { min: 20, discountPercent: 10 },
      { min: 5, discountPercent: 0 },
      { min: 0, discountPercent: 0 }
    ],
    
    // Default subscription discount
    defaultSubscriptionDiscount: 0.10, // 10%

    // Show "Only X left in stock" once addable stock drops to this or below
    lowStockThreshold: 10
  };

  /**
   * Bundle Builder Class
   */
  class BundleBuilder {
    constructor(containerId) {
      this.container = document.getElementById(containerId);
      if (!this.container) {
        return;
      }

      // State
      this.selections = {};
      this.cartQty = {}; // variantId -> qty already in cart (caps addable stock)
      this.isSubscription = false;
      this.subscriptionDiscount = CONFIG.defaultSubscriptionDiscount;
      this.deliveryFrequency = null;
      this.bulkDiscounts = this.parseBulkDiscounts();
      
      // Load translations for this bundle instance
      this.translations = this.loadTranslations(containerId);
      
      // Product type configuration
      this.productType = this.translations.productType || 'cookie';
      this.showServings = this.translations.showServings || false;
      this.productTypeTerms = this.translations.productTypeTerms || { singular: 'item', plural: 'items' };
      this.servingsSuffix = this.translations.servingsSuffix || 'servings';
      
      // DOM elements
      this.flavorCards = this.container.querySelectorAll('.flavor-card');
      this.subscriptionOptions = this.container.querySelectorAll('.purchase-option-card input[type="radio"]');
      this.addToCartBtn = this.container.querySelector('.bundle-add-to-cart');
      this.pricingHelpText = this.container.querySelector('.pricing-help-text');
      this.errorMessageEl = this.container.querySelector('.bundle-error-message');
      this.deliveryFrequencySelector = this.container.querySelector('.delivery-frequency-selector');
      this.deliveryFrequencySelect = this.container.querySelector('.delivery-frequency-select');
      this.subscriptionInfoBox = this.container.querySelector('.subscription-info-box');
      
      // New elements for dual pricing display
      this.oneTimePriceEl = this.container.querySelector('.purchase-option-card[data-subscription-type="one-time"] .option-price-value');
      this.oneTimeCookieCountEl = this.container.querySelector('.purchase-option-card[data-subscription-type="one-time"] .option-cookie-count');
      this.subscribePriceEl = this.container.querySelector('.purchase-option-card[data-subscription-type="subscribe"] .option-price-value');
      this.subscribeOriginalPriceEl = this.container.querySelector('.purchase-option-card[data-subscription-type="subscribe"] .option-price-original');
      this.subscribeCookieCountEl = this.container.querySelector('.purchase-option-card[data-subscription-type="subscribe"] .option-cookie-count');
      this.subscriptionBannerEl = this.container.querySelector('.purchase-option-banner');
      this.subscriptionDiscountTextEl = this.container.querySelector('.subscription-discount-text');
      this.addToCartLabel = this.addToCartBtn ? this.addToCartBtn.textContent.trim() : '';
      
      // Initialize
      this.init();
    }

    init() {
      this.bindEvents();
      this.populateFrequencyOptions();
      this.fetchSubscriptionDiscount();
      this.updateUI();

      // Stock caps depend on what's already in the cart
      this.refreshCartQuantities();
      document.addEventListener('pa-cart:updated', (e) => {
        if (e.detail && e.detail.cart) this.applyCartQuantities(e.detail.cart);
      });
    }

    /**
     * Fetch /cart.js and apply in-cart quantities to the stock caps
     */
    async refreshCartQuantities() {
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        if (res.ok) this.applyCartQuantities(await res.json());
      } catch (e) {
        // Caps fall back to raw stock; the server still rejects oversells
      }
    }

    applyCartQuantities(cart) {
      const cartQty = {};
      (cart.items || []).forEach(item => {
        const id = String(item.variant_id);
        cartQty[id] = (cartQty[id] || 0) + item.quantity;
      });
      this.cartQty = cartQty;

      // Selections that no longer fit (stock now sitting in the cart) get clamped
      this.flavorCards.forEach(card => {
        const qty = this.selections[card.dataset.variantId] || 0;
        const max = this.getMaxQty(card);
        if (qty > max) this.setQuantity(card, max);
      });
      this.updateUI();
    }

    /**
     * Raw stock from Liquid (tracked + deny-oversell variants only), Infinity if uncapped
     */
    getStock(card) {
      const raw = card.dataset.stockMax;
      if (raw === undefined || raw === '') return Infinity;
      const stock = parseInt(raw, 10);
      return isNaN(stock) ? Infinity : Math.max(0, stock);
    }

    /**
     * How many more of this flavor can be added: stock minus what's already in the cart
     */
    getMaxQty(card) {
      if (card.classList.contains('flavor-card--sold-out')) return 0;
      const inCart = this.cartQty[card.dataset.variantId] || 0;
      return Math.max(0, this.getStock(card) - inCart);
    }

    /**
     * Disable + at the cap, disable Add when nothing is left, show low-stock note
     */
    updateStockUI() {
      this.flavorCards.forEach(card => {
        if (card.classList.contains('flavor-card--sold-out')) return;
        const stock = this.getStock(card);
        if (stock === Infinity) return;

        const max = this.getMaxQty(card);
        const qty = this.selections[card.dataset.variantId] || 0;
        const addBtn = card.querySelector('.flavor-add-btn');
        const plusBtn = card.querySelector('.qty-plus');
        const qtyInput = card.querySelector('.qty-input');
        const note = card.querySelector('[data-stock-note]');

        if (addBtn) addBtn.disabled = max === 0;
        if (plusBtn) plusBtn.disabled = qty >= max;
        if (qtyInput) qtyInput.max = max;

        if (!note) return;
        let text = '';
        if (max === 0 && stock > 0) {
          text = this.translations.stockAllInCart || '';
        } else if (max <= CONFIG.lowStockThreshold || qty >= max) {
          text = (this.translations.stockOnlyLeft || 'Only __amount__ left in stock').replace('__amount__', max);
        }
        note.textContent = text;
        note.hidden = !text;
      });
    }

    /**
     * Load translations for this bundle instance
     */
    loadTranslations(containerId) {
      // Get translations from window object (set by Liquid)
      const translations = window.bundleTranslations && window.bundleTranslations[containerId];
      
      if (!translations) {
        // Fallback to English defaults
        return {
          currentLang: 'en',
          please_select_minimum: 'Please select at least {count} items',
          add_items_save: 'Add {count} items and save {percent}%',
          add_more_save: 'Add {count} more items and save another {percent}%',
          saving_max: "You're saving {percent}% 🎉!",
          save_on_delivery: 'Save 10% on every delivery',
          discount_all_orders: '10% off all recurring orders'
        };
      }
      
      return translations;
    }

    /**
     * Get product type term with proper pluralization
     */
    getProductTypeTerm(count) {
      return count === 1 ? this.productTypeTerms.singular : this.productTypeTerms.plural;
    }

    /**
     * Calculate total servings based on weight and portion size
     */
    calculateTotalServings() {
      if (!this.showServings) return 0;
      
      let totalServings = 0;
      this.flavorCards.forEach(card => {
        const variantId = card.dataset.variantId;
        const quantity = this.selections[variantId] || 0;
        const weight = parseFloat(card.dataset.weight);
        const portionSize = parseFloat(card.dataset.portionSize);
        
        if (quantity > 0 && weight && portionSize) {
          const servingsForThis = (quantity * weight) / portionSize;
          totalServings += servingsForThis;
        }
      });
      
      return Math.floor(totalServings);
    }

    /**
     * Parse bulk discount configuration from data attribute
     * Format: "minQty:discountPercent,minQty:discountPercent"
     * Example: "5:0,20:10,40:18" means 5+ = 0% off, 20+ = 10% off, 40+ = 18% off
     * Returns empty array if no bulk discounts configured
     */
    parseBulkDiscounts() {
      // Get from data attribute (metafield or manual configuration)
      const bulkDiscountsAttr = this.container.dataset.bulkDiscounts;
      
      // If no bulk discounts attribute or empty string, return empty array
      if (!bulkDiscountsAttr || bulkDiscountsAttr.trim() === '') {
        return [];
      }

      try {
        const discounts = bulkDiscountsAttr.split(',').map(tier => {
          const [min, discountPercent] = tier.split(':').map(val => parseFloat(val.trim()));
          return { min, discountPercent };
        });

        // Sort by minimum quantity (descending) for proper tier matching
        discounts.sort((a, b) => b.min - a.min);
        
        return discounts;
      } catch (error) {
        return [];
      }
    }

    /**
     * Get the minimum tier quantity required (lowest non-zero tier)
     */
    getMinTierQuantity() {
      // If no bulk discounts, return 1 as minimum
      if (this.bulkDiscounts.length === 0) {
        return 1;
      }
      
      // Find the smallest non-zero minimum quantity from tiers
      const nonZeroTiers = this.bulkDiscounts.filter(tier => tier.min > 0);
      if (nonZeroTiers.length === 0) {
        return 1; // Fallback to 1 if no valid tiers
      }
      return Math.min(...nonZeroTiers.map(tier => tier.min));
    }

    /**
     * Bind all event listeners
     */
    bindEvents() {
      // Flavor card interactions
      this.flavorCards.forEach(card => {
        const addBtn = card.querySelector('.flavor-add-btn');
        const minusBtn = card.querySelector('.qty-minus');
        const plusBtn = card.querySelector('.qty-plus');
        const qtyInput = card.querySelector('.qty-input');

        // Make entire card clickable for initial add (except image and controls)
        card.addEventListener('click', (e) => {
          if (e.target.closest('.flavor-quantity-selector') || e.target.closest('.flavor-add-btn') || e.target.closest('.flavor-card__image')) return;
          const btn = card.querySelector('.flavor-add-btn');
          if (btn && btn.style.display !== 'none') {
            this.handleAddClick(card);
          }
        });

        if (addBtn) {
          addBtn.addEventListener('click', () => this.handleAddClick(card));
        }

        if (minusBtn) {
          minusBtn.addEventListener('click', () => this.handleQuantityChange(card, -1));
        }

        if (plusBtn) {
          plusBtn.addEventListener('click', () => this.handleQuantityChange(card, 1));
        }

        if (qtyInput) {
          // Track if we're currently processing to prevent double-updates
          let isProcessing = false;
          
          // Handle typing in the input field - update state and UI live
          qtyInput.addEventListener('input', (e) => {
            // Only update if not empty (let blur handle empty/invalid values)
            const rawValue = e.target.value;
            if (rawValue === '' || rawValue === null) {
              return; // Don't update while user is clearing the field
            }
            
            let value = parseInt(rawValue) || 0;
            const variantId = card.dataset.variantId;

            // Cap at available stock while typing
            const max = this.getMaxQty(card);
            if (value > max) {
              value = max;
              e.target.value = max;
            }
            
            // Update internal state
            if (value > 0) {
              this.selections[variantId] = value;
            } else {
              delete this.selections[variantId];
            }
            
            // Update UI live as user types
            this.updateUI();
          });
          
          // Handle blur (when user clicks away) - finalize the value
          qtyInput.addEventListener('blur', (e) => {
            if (isProcessing) return;
            isProcessing = true;
            
            // Use a small delay to ensure we get the final value
            // This prevents race conditions with change events
            setTimeout(() => {
              const value = parseInt(qtyInput.value) || 0;
              this.setQuantity(card, value);
              isProcessing = false;
            }, 10);
          });
          
          // Handle Enter key - blur to finalize the value
          qtyInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
              e.target.blur();
            }
          });
          
          // Note: Removed redundant 'change' event handler
          // The blur handler already handles value finalization
          // Having both caused race conditions where values could be processed twice
        }
      });

      // Subscription toggle
      this.subscriptionOptions.forEach(option => {
        option.addEventListener('change', () => {
          this.isSubscription = option.value === 'subscribe';
          this.toggleSubscriptionElements();
          this.updateUI();
        });
      });
      
      // Delivery frequency selector
      if (this.deliveryFrequencySelect) {
        this.deliveryFrequencySelect.addEventListener('change', (e) => {
          this.deliveryFrequency = e.target.value;
          this.updateUI();
        });
      }

      // Add to cart button
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
      }
    }

    /**
     * Populate frequency options from selling plans
     */
    populateFrequencyOptions() {
      if (!this.deliveryFrequencySelect) return;
      
      // Clear existing options
      this.deliveryFrequencySelect.innerHTML = '';
      
      // Try to get selling plans from hidden selling plans data element
      const plansContainer = document.querySelector('.selling-plans-data');

      if (plansContainer) {
        const sellingPlans = plansContainer.querySelectorAll('input[name="selling_plan"]');
        
        if (sellingPlans.length > 0) {
          sellingPlans.forEach((plan, index) => {
            const planId = plan.value;
            const frequency = plan.dataset.frequency;
            
            // Get the label text
            const label = plan.nextElementSibling || plan.closest('label');
            let labelText = label ? label.textContent.trim() : '';
            
            // If we don't have label text but have frequency, construct it
            if (!labelText && frequency) {
              labelText = `Every ${frequency} weeks`;
            }
            
            if (labelText || frequency) {
              const option = document.createElement('option');
              option.value = frequency || planId;
              option.textContent = labelText || `Every ${frequency} weeks`;
              option.dataset.planId = planId;
              
              this.deliveryFrequencySelect.appendChild(option);
              
              // Auto-select first option
              if (index === 0) {
                option.selected = true;
                this.deliveryFrequency = option.value;
              }
            }
          });
          
          return;
        }
      }
      
      // Fallback: add default options if selling plans data not found
      const defaultOptions = [
        { value: '4', text: 'Every 4 weeks' },
        { value: '6', text: 'Every 6 weeks' },
        { value: '8', text: 'Every 8 weeks' }
      ];
      
      defaultOptions.forEach((opt, index) => {
        const option = document.createElement('option');
        option.value = opt.value;
        option.textContent = opt.text;
        this.deliveryFrequencySelect.appendChild(option);
        
        if (index === 0) {
          option.selected = true;
          this.deliveryFrequency = opt.value;
        }
      });
    }

    /**
     * Toggle subscription-related UI elements
     */
    toggleSubscriptionElements() {
      if (this.deliveryFrequencySelector) {
        this.deliveryFrequencySelector.style.display = this.isSubscription ? 'block' : 'none';
      }
      
      // Auto-select first frequency when switching to subscription
      if (this.isSubscription && this.deliveryFrequencySelect && !this.deliveryFrequency) {
        if (this.deliveryFrequencySelect.options.length > 0) {
          this.deliveryFrequencySelect.selectedIndex = 0;
          this.deliveryFrequency = this.deliveryFrequencySelect.value;
        }
      }
      
      // Reset delivery frequency when switching to one-time
      if (!this.isSubscription) {
        this.deliveryFrequency = null;
      }
    }

    /**
     * Handle "Add" button click
     */
    handleAddClick(card) {
      const addBtn = card.querySelector('.flavor-add-btn');
      const qtySelector = card.querySelector('.flavor-quantity-selector');

      if (this.getMaxQty(card) === 0) return;

      if (addBtn && qtySelector) {
        addBtn.style.display = 'none';
        qtySelector.style.display = 'flex';
        
        // Set initial quantity to 1
        this.setQuantity(card, 1);
      }
    }

    /**
     * Handle quantity change (+ or -)
     */
    handleQuantityChange(card, delta) {
      const variantId = card.dataset.variantId;
      const currentQty = this.selections[variantId] || 0;
      const newQty = Math.max(0, currentQty + delta);
      
      this.setQuantity(card, newQty);
    }

    /**
     * Set quantity for a flavor
     */
    setQuantity(card, quantity) {
      const variantId = card.dataset.variantId;
      const qtyInput = card.querySelector('.qty-input');
      const addBtn = card.querySelector('.flavor-add-btn');
      const qtySelector = card.querySelector('.flavor-quantity-selector');
      
      quantity = Math.min(Math.max(0, parseInt(quantity) || 0), this.getMaxQty(card));
      
      // Update state
      if (quantity > 0) {
        this.selections[variantId] = quantity;
      } else {
        delete this.selections[variantId];
      }
      
      // Update input
      if (qtyInput) {
        qtyInput.value = quantity;
      }
      
      // Toggle between add button and quantity selector
      if (quantity === 0 && addBtn && qtySelector) {
        addBtn.style.display = 'block';
        qtySelector.style.display = 'none';
      }
      
      this.updateUI();
    }

    /**
     * Calculate total quantity
     */
    getTotalQuantity() {
      return Object.values(this.selections).reduce((sum, qty) => sum + qty, 0);
    }

    /**
     * Get bulk discount percentage based on total quantity
     */
    getBulkDiscountPercent(totalQty) {
      for (const tier of this.bulkDiscounts) {
        if (totalQty >= tier.min) {
          return tier.discountPercent;
        }
      }
      return 0;
    }

    /**
     * Calculate total price using actual Shopify variant prices + bulk discount + subscription discount
     */
    calculateTotal() {
      const totalQty = this.getTotalQuantity();
      const bulkDiscountPercent = this.getBulkDiscountPercent(totalQty);
      
      let subtotal = 0;
      
      // Calculate subtotal using actual Shopify variant prices
      this.flavorCards.forEach(card => {
        const variantId = card.dataset.variantId;
        const quantity = this.selections[variantId];
        
        if (quantity && quantity > 0) {
          const basePrice = parseFloat(card.dataset.price);
          if (basePrice) {
            subtotal += basePrice * quantity;
          }
        }
      });
      
      // Apply bulk discount
      let total = subtotal;
      if (bulkDiscountPercent > 0) {
        total = subtotal * (1 - bulkDiscountPercent / 100);
      }
      
      const afterBulkDiscount = total;
      
      // Apply subscription discount
      if (this.isSubscription) {
        total = total * (1 - this.subscriptionDiscount);
      }
      
      return {
        total: total,
        originalTotal: subtotal,
        afterBulkDiscount: afterBulkDiscount,
        bulkDiscountPercent: bulkDiscountPercent,
        quantity: totalQty
      };
    }

    /**
     * Update UI with current state
     */
    updateUI() {
      const totals = this.calculateTotal();
      const totalServings = this.calculateTotalServings();
      const productTypeTerm = this.getProductTypeTerm(totals.quantity);
      
      // Build subtitle text with product type and optional servings
      let subtitleText = `${totals.quantity} ${productTypeTerm}`;
      if (this.showServings && totalServings > 0) {
        subtitleText += ` - ${totalServings} ${this.servingsSuffix}`;
      }
      
      // Update counts in both option cards
      if (this.oneTimeCookieCountEl) {
        // Update the count number
        this.oneTimeCookieCountEl.textContent = totals.quantity;
        
        // Update the product type text with proper pluralization
        const productTypeTextEl = this.oneTimeCookieCountEl.parentElement.querySelector('.product-type-text');
        if (productTypeTextEl) {
          const productTypeTerm = this.getProductTypeTerm(totals.quantity);
          let displayText = productTypeTerm;
          
          // Add servings if enabled
          if (this.showServings && totalServings > 0) {
            displayText += ` - ${totalServings} ${this.servingsSuffix}`;
          }
          
          productTypeTextEl.textContent = displayText;
        }
      }
      if (this.subscribeCookieCountEl) {
        // Update the count number
        this.subscribeCookieCountEl.textContent = totals.quantity;
        
        // Update the product type text with proper pluralization
        const productTypeTextEl = this.subscribeCookieCountEl.parentElement.querySelector('.product-type-text');
        if (productTypeTextEl) {
          const productTypeTerm = this.getProductTypeTerm(totals.quantity);
          let displayText = productTypeTerm;
          
          // Add servings if enabled
          if (this.showServings && totalServings > 0) {
            displayText += ` - ${totalServings} ${this.servingsSuffix}`;
          }
          
          productTypeTextEl.textContent = displayText;
        }
      }
      
      // Calculate prices for BOTH options
      const oneTimePrice = totals.afterBulkDiscount; // Bulk discount only
      
      // ALWAYS calculate subscription price with discount, regardless of selection
      const subscribePrice = totals.afterBulkDiscount * (1 - this.subscriptionDiscount);
      
      // Update one-time price
      if (this.oneTimePriceEl) {
        this.oneTimePriceEl.textContent = window.paFormatMoney(Math.round(oneTimePrice * 100));
      }
      
      // Update subscribe price - always show both original and discounted
      if (this.subscribePriceEl) {
        this.subscribePriceEl.textContent = window.paFormatMoney(Math.round(subscribePrice * 100));
      }
      
      // Always show original price on subscribe option (strikethrough)
      if (this.subscribeOriginalPriceEl) {
        this.subscribeOriginalPriceEl.textContent = window.paFormatMoney(Math.round(oneTimePrice * 100));
        this.subscribeOriginalPriceEl.style.display = 'inline';
      }
      
      // Update help text for bulk discount tiers (only if bulk discounts exist)
      if (this.pricingHelpText) {
        // Hide pricing help text if no bulk discounts configured
        if (this.bulkDiscounts.length === 0) {
          this.pricingHelpText.parentElement.style.display = 'none';
        } else {
          this.pricingHelpText.parentElement.style.display = 'block';
          
          const minTierQty = this.getMinTierQuantity();
          const nextTier = this.getNextDiscountTier(totals.quantity);
          const currentDiscountPercent = totals.bulkDiscountPercent;
          
          if (totals.quantity < minTierQty && nextTier && nextTier.discountPercent > 0) {
            // Below first discount tier - show upsell message
            const needed = nextTier.min - totals.quantity;
            const productType = this.getProductTypeTerm(needed);
            this.pricingHelpText.textContent = this.translations.add_items_save
              .replace('{count}', needed)
              .replace('{product_type}', productType)
              .replace('{percent}', nextTier.discountPercent);
          } else if (totals.quantity < minTierQty) {
            // Below minimum tier with no discount available - show requirement
            const productType = this.getProductTypeTerm(minTierQty);
            this.pricingHelpText.textContent = this.translations.please_select_minimum
              .replace('{count}', minTierQty)
              .replace('{product_type}', productType);
          } else if (nextTier) {
            const needed = nextTier.min - totals.quantity;
            const additionalDiscount = nextTier.discountPercent - currentDiscountPercent;
            const productType = this.getProductTypeTerm(needed);
            
            if (currentDiscountPercent === 0) {
              // Scenario 2: At minimum tier but not saving yet - "Add X items and save Y%"
              this.pricingHelpText.textContent = this.translations.add_items_save
                .replace('{count}', needed)
                .replace('{product_type}', productType)
                .replace('{percent}', nextTier.discountPercent);
            } else {
              // Scenario 3: Already saving, can save more - "Add X more items and save another Z%"
              this.pricingHelpText.textContent = this.translations.add_more_save
                .replace('{count}', needed)
                .replace('{product_type}', productType)
                .replace('{percent}', additionalDiscount);
            }
          } else {
            // Scenario 4: At max tier, show current savings
            this.pricingHelpText.textContent = this.translations.saving_max
              .replace('{percent}', currentDiscountPercent);
          }
        }
      }
      
      // Update "Add" button prices with current discounts
      this.updateAddButtonPrices();

      // Stock caps / low-stock notes
      this.updateStockUI();
      
      // Enable/disable add to cart button
      if (this.addToCartBtn) {
        // If the first tier has a discount (e.g. 4:10), allow ordering from 1+ item
        // If the first tier has 0% discount (e.g. 5:0), enforce it as the minimum order quantity
        const minTierQty = this.getMinTierQuantity();
        const firstTierIsDiscount = this.bulkDiscounts.length > 0 &&
          this.bulkDiscounts.every(tier => tier.discountPercent > 0);
        const minOrderQty = firstTierIsDiscount ? 1 : minTierQty;
        const hasMinItems = totals.quantity >= minOrderQty;
        const hasFrequency = !this.isSubscription || this.deliveryFrequency;
        const canAddToCart = hasMinItems && hasFrequency;
        
        this.addToCartBtn.disabled = !canAddToCart;
        
        if (!canAddToCart) {
          this.addToCartBtn.classList.add('disabled');
        } else {
          this.addToCartBtn.classList.remove('disabled');
        }
      }
    }

    /**
     * Get the next available bulk discount tier
     */
    getNextDiscountTier(currentQty) {
      // Find all tiers that are higher than current quantity
      const higherTiers = this.bulkDiscounts.filter(tier => tier.min > currentQty);
      
      // If no higher tiers, we're at the max
      if (higherTiers.length === 0) {
        return null;
      }
      
      // Return the tier with the smallest minimum (the next tier)
      return higherTiers.reduce((lowest, tier) => 
        tier.min < lowest.min ? tier : lowest
      );
    }

    /**
     * Update all "Add" button prices with current discounts (bulk + subscription)
     */
    updateAddButtonPrices() {
      const currentQty = this.getTotalQuantity();
      
      // Calculate what the bulk discount would be if we added one more item
      const nextQty = currentQty + 1;
      const bulkDiscountPercent = this.getBulkDiscountPercent(nextQty);
      
      this.flavorCards.forEach(card => {
        const addBtn = card.querySelector('.flavor-add-btn');
        const priceValueEl = addBtn ? addBtn.querySelector('.price-value') : null;
        
        if (priceValueEl) {
          // Get the actual Shopify variant price from the card data
          const basePrice = parseFloat(card.dataset.price) || parseFloat(addBtn.dataset.price);
          
          if (!basePrice) {
            return;
          }
          
          // Apply bulk discount
          let finalPrice = basePrice;
          if (bulkDiscountPercent > 0) {
            finalPrice = basePrice * (1 - bulkDiscountPercent / 100);
          }
          
          // Apply subscription discount if active
          if (this.isSubscription) {
            finalPrice = finalPrice * (1 - this.subscriptionDiscount);
          }
          
          // Update the price display (active currency, snippets/pa-money.liquid)
          priceValueEl.textContent = window.paFormatMoney(Math.round(finalPrice * 100));
        }
      });
    }

    /**
     * Fetch subscription discount from selling plans
     */
    async fetchSubscriptionDiscount() {
      try {
        const plansContainer = document.querySelector('.selling-plans-data');
        if (plansContainer) {
          const discountEl = plansContainer.querySelector('[data-discount-percentage]');
          if (discountEl) {
            const discount = parseFloat(discountEl.dataset.discountPercentage);
            if (!isNaN(discount)) {
              this.subscriptionDiscount = discount / 100;
              
              // Banner text and discount text are set with proper translations in Liquid
            }
          }
        }
        
        // Fallback: use default discount from data attribute or config
        if (this.subscriptionDiscount === CONFIG.defaultSubscriptionDiscount) {
          const defaultDiscount = this.container.dataset.defaultSubscriptionDiscount;
          if (defaultDiscount) {
            this.subscriptionDiscount = parseFloat(defaultDiscount) / 100;
            // Discount text is already properly set in Liquid with translations
          }
        }
      } catch (error) {
        // Could not fetch subscription discount
      }
    }

    /**
     * Handle add to cart
     */
    async handleAddToCart() {
      // Use same minimum logic as updateUI(): discount-only tiers allow ordering from 1+
      const minTierQty = this.getMinTierQuantity();
      const firstTierIsDiscount = this.bulkDiscounts.length > 0 &&
        this.bulkDiscounts.every(tier => tier.discountPercent > 0);
      const minOrderQty = firstTierIsDiscount ? 1 : minTierQty;

      if (this.getTotalQuantity() < minOrderQty) {
        this.showError(`Please select at least ${minOrderQty} items`);
        return;
      }
      
      // Validate delivery frequency for subscriptions
      if (this.isSubscription && !this.deliveryFrequency) {
        this.showError('Please select a delivery frequency');
        return;
      }

      this.addToCartBtn.disabled = true;
      this.addToCartBtn.textContent = this.translations.addingLabel || 'Adding...';

      try {
        await this.addBundleToCart();

        // Success - update cart drawer/notification
        this.addToCartBtn.textContent = this.translations.addedLabel || 'Added!';
        setTimeout(() => {
          this.addToCartBtn.textContent = this.addToCartLabel;
          this.updateUI();
        }, 2000);

        // Added items now sit in the cart, so the stock caps shrink
        this.refreshCartQuantities();
        
        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:update'));
        
        // Open cart drawer if available
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
        
      } catch (error) {
        // Shopify's 422 description is localized ("You can only add 1 ..."), prefer it
        this.showError(error.message || this.translations.addFailed || 'Failed to add to cart. Please try again.');
        this.addToCartBtn.textContent = this.addToCartLabel;
        this.updateUI();
        this.refreshCartQuantities();
      }
    }

    /**
     * Add bundle to cart using Shopify Cart API
     */
    async addBundleToCart() {
      const items = [];
      
      // Build cart items array
      this.flavorCards.forEach(card => {
        const variantId = card.dataset.variantId;
        const quantity = this.selections[variantId];
        
        if (quantity && quantity > 0) {
          const item = {
            id: parseInt(variantId),
            quantity: parseInt(quantity),
            properties: {}
          };
          
          // Add subscription properties. If the cart already has an active
          // subscription in the drawer, align this line with that cadence —
          // even when the user selected "one-time" or a different frequency
          // here on the PDP. Keeps a multi-item cart on a single interval.
          const cartSub = window.paCart && typeof window.paCart.getCartSubState === 'function'
            ? window.paCart.getCartSubState()
            : null;
          const shouldSubscribe = (cartSub && cartSub.active) || this.isSubscription;
          if (shouldSubscribe) {
            const sellingPlanId = this.getSellingPlanId();
            if (sellingPlanId) {
              item.selling_plan = parseInt(sellingPlanId);
            }
          }
          
          items.push(item);
        }
      });
      
      if (items.length === 0) {
        throw new Error('No items selected');
      }
      
      // Check if UpCart is active
      const isUpCartActive = !!(
        window.UpCart ||
        window.upcart ||
        window.Upcart ||
        document.querySelector('script[src*="upcart"]') ||
        document.querySelector('link[href*="upcart"]')
      );
      
      // Get cart drawer/notification for section updates
      const cart = !isUpCartActive && (document.querySelector('cart-notification') || document.querySelector('cart-drawer'));
      
      // Build FormData for UpCart compatibility
      const formData = new FormData();
      items.forEach((item, index) => {
        formData.append(`items[${index}][id]`, item.id);
        formData.append(`items[${index}][quantity]`, item.quantity);
        
        if (item.selling_plan) {
          formData.append(`items[${index}][selling_plan]`, item.selling_plan);
        }
        
        if (item.properties) {
          Object.entries(item.properties).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              formData.append(`items[${index}][properties][${key}]`, String(value));
            }
          });
        }
      });
      
      // Prepare sections for cart drawer update
      let url = '/cart/add';
      if (!isUpCartActive && cart && typeof cart.getSectionsToRender === 'function') {
        const sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        const sectionsUrl = window.location.pathname;
        url += `?sections=${encodeURIComponent(sections)}&sections_url=${encodeURIComponent(sectionsUrl)}`;
      }
      
      // Add to cart
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData
      });
      
      // Retry without sections param if it fails (some apps don't support it)
      if (!response.ok && response.status === 400 && url.includes('sections=')) {
        const retryResponse = await fetch('/cart/add', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
          },
          body: formData
        });
        
        if (!retryResponse.ok) {
          throw new Error(await this.readCartError(retryResponse));
        }
        
        return await retryResponse.json();
      }
      
      if (!response.ok) {
        throw new Error(await this.readCartError(response));
      }
      
      const data = await response.json();
      
      // Update cart drawer if present
      if (!isUpCartActive && cart && data.sections) {
        cart.renderContents(data);
      }
      
      return data;
    }

    /**
     * Human-readable message from a failed /cart/add response ('' if none)
     */
    async readCartError(response) {
      try {
        const data = await response.json();
        return (data && typeof data.description === 'string') ? data.description : '';
      } catch (e) {
        return '';
      }
    }

    /**
     * Get selling plan ID based on delivery frequency
     */
    getSellingPlanId() {
      // Prefer cart's active subscription interval over the PDP's selection.
      // Keeps a multi-item cart on one cadence; see addBundleToCart caller.
      const cartSub = window.paCart && typeof window.paCart.getCartSubState === 'function'
        ? window.paCart.getCartSubState()
        : null;
      const useCartInterval = !!(cartSub && cartSub.active);
      const effectiveInterval = useCartInterval
        ? String(cartSub.interval)
        : (this.isSubscription ? this.deliveryFrequency : null);
      if (!effectiveInterval) return null;

      // Match helper: pulls the first digit from an option/plan label so we
      // work across EN ("Every 4 weeks"), FI ("Joka 4 viikkoa"), DE ("Alle 4
      // Wochen") without per-language branches.
      const intervalMatches = (text) => {
        if (!text) return false;
        const m = String(text).match(/(\d+)/);
        return m && m[1] === effectiveInterval;
      };

      // Method 1: Selected dropdown option. Only trust it when the user's PDP
      // choice matches effectiveInterval — otherwise its planId points at the
      // wrong cadence.
      if (!useCartInterval && this.deliveryFrequencySelect) {
        const selectedOption = this.deliveryFrequencySelect.options[this.deliveryFrequencySelect.selectedIndex];
        if (selectedOption && selectedOption.dataset.planId) {
          return selectedOption.dataset.planId;
        }
      }

      // Method 2: Hidden selling-plans-data <input> radios rendered by the
      // snippet. Each has data-option1 = full option value like "Joka 4 viikkoa".
      const plansContainer = document.querySelector('.selling-plans-data');
      if (plansContainer) {
        const allPlans = plansContainer.querySelectorAll('input[name="selling_plan"]');
        for (const plan of allPlans) {
          if (intervalMatches(plan.dataset.option1)) return plan.value;
          const label = plan.closest('label') || plan.nextElementSibling;
          if (label && intervalMatches(label.textContent)) return plan.value;
        }
        // Fallback to first plan only for PDP-driven adds. When we're
        // overriding with cart interval, no match means we'd rather add
        // as one-time than as the wrong cadence.
        if (!useCartInterval && allPlans.length > 0) {
          return allPlans[0].value;
        }
      }

      // Method 3: Inline product JSON.
      const productScripts = document.querySelectorAll('script[type="application/json"]');
      for (const script of productScripts) {
        try {
          const data = JSON.parse(script.textContent);
          if (data.selling_plan_groups && data.selling_plan_groups.length > 0) {
            for (const group of data.selling_plan_groups) {
              if (group.selling_plans) {
                for (const plan of group.selling_plans) {
                  const optVal = plan.options && plan.options[0] && (plan.options[0].value || plan.options[0]);
                  if (intervalMatches(plan.name) || intervalMatches(optVal)) return plan.id;
                }
                if (!useCartInterval && group.selling_plans.length > 0) {
                  return group.selling_plans[0].id;
                }
              }
            }
          }
        } catch (e) {
          // Not a product JSON, continue
        }
      }

      // Method 4: ShopifyAnalytics meta.
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
        const sellingPlanGroups = window.ShopifyAnalytics.meta.product.selling_plan_groups;
        if (sellingPlanGroups && sellingPlanGroups.length > 0) {
          for (const group of sellingPlanGroups) {
            if (group.selling_plans) {
              for (const plan of group.selling_plans) {
                if (intervalMatches(plan.name)) return plan.id;
              }
              if (!useCartInterval && group.selling_plans.length > 0) {
                return group.selling_plans[0].id;
              }
            }
          }
        }
      }
      
      // Final fallback
      return null;
    }

    /**
     * Show error message
     */
    showError(message) {
      if (this.errorMessageEl) {
        this.errorMessageEl.textContent = message;
        this.errorMessageEl.style.display = 'block';
        
        setTimeout(() => {
          this.errorMessageEl.style.display = 'none';
        }, 5000);
      }
    }
  }

  /**
   * Initialize a bundle builder instance
   */
  window.CustomBundleBuilder.init = function(containerId) {
    return new BundleBuilder(containerId);
  };

})();

